// Lo que el Brain hizo para responder (R2, CA1).
//
// Un dato sin procedencia es indistinguible de uno inventado. El 2026-08-24 el
// Brain conto cuatro documentos con su desglose exacto por tipo **sin haber
// llamado a nada**, y con una cifra ya desfasada: la respuesta se leia igual de
// segura que una comprobada. Esto es lo que las separa.
//
// El diseño sale del prototipo de `dont-merge-front-vision` -fila plegada, se
// abre al pulsar-, conectado al rastro de verdad en vez de a datos simulados.

import { useState } from 'react'
import { FilaDesplegable } from '@/components/ui/fila-desplegable'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { ArrowUpRight } from 'lucide-react'
import { AlertCircle, CheckCircle2, ChevronRight, HelpCircle, PauseCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { NEEDS_ANSWER, NECESITA_CONFIRMACION, type BrainToolCall } from '@/services/brainApi'
import { EstadisticasDeHerramienta } from './EstadisticasDeHerramienta'
import { ListaDeEntidades } from './ListaDeEntidades'
import { cargaUtil, pareceEstadisticas, pareceListaDeEntidades } from './formas'
import { seccionDe } from './seccionDe'
import { RotuloDeSeccion } from '@/components/ui/rotulo-de-seccion'

interface Props {
  llamadas: BrainToolCall[]
  /** Nombre legible por nombre tecnico. Sin el se enseña el tecnico. */
  nombres?: Map<string, string>
}

const ESTADOS = {
  success: { icono: CheckCircle2, color: 'text-success', clave: 'brain.estado_hecho' },
  error: { icono: AlertCircle, color: 'text-destructive', clave: 'brain.estado_fallo' },
  [NECESITA_CONFIRMACION]: {
    icono: PauseCircle,
    color: 'text-warning',
    clave: 'brain.estado_esperando',
  },
  // Asking is not «done»: without this the question showed as a finished call.
  [NEEDS_ANSWER]: {
    icono: HelpCircle,
    color: 'text-primary',
    clave: 'brain.estado_pregunta',
  },
} as const

function estadoDe(llamada: BrainToolCall) {
  return ESTADOS[llamada.status as keyof typeof ESTADOS] ?? ESTADOS.success
}

/** El resultado en algo que se pueda leer, sin volcar un JSON de 60 KB. */
function resumenDelResultado(llamada: BrainToolCall): string | null {
  const bruto = llamada.result
  if (bruto === undefined || bruto === null) return null
  const texto = typeof bruto === 'string' ? bruto : JSON.stringify(bruto, null, 2)
  return texto.length > 1200 ? `${texto.slice(0, 1200)}\n…` : texto
}

/** El resultado pintado, si su forma se reconoce. Si no, `null` y se cae al
 * JSON: enseñar el crudo es peor que una tarjeta, pero mucho mejor que
 * adivinar. */
function enPalabras(resultado: unknown) {
  const datos = cargaUtil(resultado)
  if (datos === null) return null
  if (pareceEstadisticas(datos)) return <EstadisticasDeHerramienta datos={datos} />
  const lista = pareceListaDeEntidades(datos)
  if (lista) return <ListaDeEntidades entidades={lista.entidades} total={lista.total} />
  return null
}

/** One tool call, folded, with what it was given and what it returned. Also
 * used inside the steps of an answer (`BrainSteps`), so a call is painted the
 * same wherever it shows up. */
export function ToolCallRow({ llamada, nombres }: { llamada: BrainToolCall; nombres?: Map<string, string> }) {
  const [abierta, setAbierta] = useState(false)
  const { t } = useTranslation()
  const { lang } = useParams()
  // Enlazar, no manejar: R10 de `brain-agente-maestro`. La pantalla lleva a
  // donde vive el resultado y alli se gestiona; no abre formularios por nadie.
  const seccion = seccionDe(llamada.tool)
  const { icono: Icono, color, clave } = estadoDe(llamada)
  const legible = llamada.accion?.nombre_visible || nombres?.get(llamada.tool) || llamada.tool
  const argumentos = Object.entries(llamada.args ?? {})
  const resultado = resumenDelResultado(llamada)
  const pintado = enPalabras(llamada.result)
  const hayDetalle = argumentos.length > 0 || Boolean(resultado)

  return (
    <div className="overflow-hidden rounded-md border">
      <FilaDesplegable
        abierta={abierta}
        onAlternar={() => setAbierta((v) => !v)}
        sinDetalle={!hayDetalle}
        cabecera={<>
        {hayDetalle ? (
          <ChevronRight
            className={cn(
              'h-3 w-3 shrink-0 text-muted-foreground transition-transform',
              abierta && 'rotate-90',
            )}
          />
        ) : (
          <span className="w-3 shrink-0" />
        )}
        <Icono className={cn('h-3.5 w-3.5 shrink-0', color)} />
        <span className="truncate text-xs font-medium">{legible}</span>
        {/* El nombre tecnico se enseña pequeño y al lado: es lo que hay que
            citar para buscar en los registros, y sin el la fila no es
            verificable. */}
        <code className="hidden truncate font-mono text-xs text-muted-foreground sm:inline">
          {llamada.tool}
        </code>
        <span className={cn('ml-auto shrink-0 text-xs', color)}>{t(clave)}</span>
        </>}
      >
      {(
        <div className="space-y-2 border-t border-border bg-muted/20 px-3 py-2">
          {argumentos.length > 0 && (
            <div>
              <RotuloDeSeccion className="mb-1">
                {t('brain.con_estos_datos')}
              </RotuloDeSeccion>
              <dl className="space-y-1">
                {argumentos.map(([clave, valor]) => (
                  <div key={clave} className="flex gap-2 font-mono text-xs">
                    <dt className="shrink-0 text-muted-foreground">{clave}</dt>
                    <dd className="min-w-0 break-all">{String(valor)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
          {seccion && (
            <Link
              to={`/${lang ?? 'es'}/${seccion.ruta}`}
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              {t(seccion.clave)}
              <ArrowUpRight className="h-3 w-3" />
            </Link>
          )}
          {(pintado || resultado) && (
            <div>
              <RotuloDeSeccion className="mb-1">
                {t('brain.lo_que_devolvio')}
              </RotuloDeSeccion>
              {pintado ?? (
                <pre className="max-h-56 overflow-auto whitespace-pre-wrap break-all font-mono text-xs text-muted-foreground">
                  {resultado}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
      </FilaDesplegable>
    </div>
  )
}

export function HerramientasUsadas({ llamadas, nombres }: Props) {
  const { t } = useTranslation()
  if (!llamadas.length) return null

  return (
    <div className="mt-2 border-t border-border pt-2">
      <p className="mb-1.5 text-xs text-muted-foreground">
        {llamadas.length === 1
          ? t('brain.consultas_una')
          : t('brain.consultas_varias', { count: llamadas.length })}
      </p>
      <div className="space-y-1">
        {llamadas.map((llamada, i) => (
          <ToolCallRow key={`${llamada.tool}-${i}`} llamada={llamada} nombres={nombres} />
        ))}
      </div>
    </div>
  )
}
